import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, CheckCircle, XCircle, Loader2, AlertCircle, LogIn } from 'lucide-react';
import { useTeams } from '../../hooks/useTeams';
import { useAuth } from '../../hooks/useAuth';

export function TeamInviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getInviteByToken, acceptInvite, declineInvite } = useTeams();

  const [invite, setInvite] = useState<any>(null);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [acting, setActing] = useState(false);
  const [result, setResult] = useState<'accepted' | 'declined' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      setLoadingInvite(true);
      const data = await getInviteByToken(token);
      setInvite(data);
      setLoadingInvite(false);
    };
    load();
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    try {
      setActing(true);
      setError(null);
      await acceptInvite(token);
      setResult('accepted');
      setTimeout(() => navigate('/dashboard/teams'), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to accept invite');
    } finally {
      setActing(false);
    }
  };

  const handleDecline = async () => {
    if (!token) return;
    try {
      setActing(true);
      setError(null);
      await declineInvite(token);
      setResult('declined');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to decline invite');
    } finally {
      setActing(false);
    }
  };

  // ── Not logged in ────────────────────────────────────────────────────────
  if (!user) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="bg-electric/10 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Users className="h-8 w-8 text-electric" />
          </div>
          <h1 className="text-2xl font-bold text-midnight mb-2">Team Invite</h1>
          <p className="text-slate mb-6">
            You've been invited to collaborate on SermonBuild. Sign in to view and accept your invite.
          </p>
          <button
            onClick={() => {
              // Preserve the token so Auth can redirect back here after sign-in
              sessionStorage.setItem('pendingInviteToken', token!);
              navigate('/auth');
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-electric text-white rounded-lg hover:bg-electric/90 font-medium"
          >
            <LogIn className="h-4 w-4" />
            Sign In to Continue
          </button>
        </div>
      </div>
    );
  }

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loadingInvite) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-electric" />
      </div>
    );
  }

  // ── Invite not found ─────────────────────────────────────────────────────
  if (!invite) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 text-crimson mx-auto mb-4" />
          <h1 className="text-xl font-bold text-midnight mb-2">Invite Not Found</h1>
          <p className="text-slate mb-6">This invite link is invalid or has already been used.</p>
          <button onClick={() => navigate('/dashboard')} className="px-4 py-2 bg-electric text-white rounded-lg">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── Expired ──────────────────────────────────────────────────────────────
  const isExpired = invite.invite_expires_at && new Date(invite.invite_expires_at) < new Date();
  if (isExpired && result === null) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-midnight mb-2">Invite Expired</h1>
          <p className="text-slate mb-6">
            This invite to <strong>{invite.teams?.name}</strong> has expired. Ask the team owner to send a new invite.
          </p>
          <button onClick={() => navigate('/dashboard')} className="px-4 py-2 bg-electric text-white rounded-lg">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── Already responded ────────────────────────────────────────────────────
  if (invite.status !== 'pending' && result === null) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 text-slate mx-auto mb-4" />
          <h1 className="text-xl font-bold text-midnight mb-2">Already Responded</h1>
          <p className="text-slate mb-6">
            You've already {invite.status} this invite.
          </p>
          <button onClick={() => navigate('/dashboard/teams')} className="px-4 py-2 bg-electric text-white rounded-lg">
            View Teams
          </button>
        </div>
      </div>
    );
  }

  // ── Result states ────────────────────────────────────────────────────────
  if (result === 'accepted') {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle className="h-12 w-12 text-emerald mx-auto mb-4" />
          <h1 className="text-xl font-bold text-midnight mb-2">You're In!</h1>
          <p className="text-slate">You've joined <strong>{invite.teams?.name}</strong>. Redirecting to Teams...</p>
        </div>
      </div>
    );
  }

  if (result === 'declined') {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <XCircle className="h-12 w-12 text-slate mx-auto mb-4" />
          <h1 className="text-xl font-bold text-midnight mb-2">Invite Declined</h1>
          <p className="text-slate mb-6">You've declined the invite to {invite.teams?.name}.</p>
          <button onClick={() => navigate('/dashboard')} className="px-4 py-2 bg-electric text-white rounded-lg">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── Main invite view ─────────────────────────────────────────────────────
  const roleLabel = invite.role === 'editor' ? 'Editor' : 'Viewer';
  const roleDesc = invite.role === 'editor'
    ? 'You can edit sermons, create slides, and add content.'
    : 'You can view and duplicate sermons.';

  return (
    <div className="min-h-screen bg-parchment flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="bg-electric/10 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Users className="h-8 w-8 text-electric" />
          </div>
          <h1 className="text-2xl font-bold text-midnight">Team Invite</h1>
          <p className="text-slate mt-1">You've been invited to join a team</p>
        </div>

        <div className="bg-sand/10 rounded-lg p-4 mb-6 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-slate">Team</span>
            <span className="font-semibold text-midnight">{invite.teams?.name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate">Your Role</span>
            <span className="font-semibold text-electric">{roleLabel}</span>
          </div>
          <div className="text-xs text-slate border-t border-sand/20 pt-3">{roleDesc}</div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-crimson/10 text-crimson rounded-lg text-sm flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleDecline}
            disabled={acting}
            className="flex-1 px-4 py-3 border border-sand/30 text-slate rounded-lg hover:bg-sand/10 disabled:opacity-50 font-medium"
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            disabled={acting}
            className="flex-1 px-4 py-3 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
          >
            {acting && <Loader2 className="h-4 w-4 animate-spin" />}
            Accept Invite
          </button>
        </div>
      </div>
    </div>
  );
}
